import React, { useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { useInfiniteQuery, useQuery } from "react-query";
import { useLocation, useParams, useRouteMatch } from "react-router-dom";

import UserContext from "contexts/userContext";
import useNavigationType from "fe-shared/src/hooks/useNavigationType";
import useEntityNavigationHandle from "fe-shared/src/hooks/useEntityNavigationHandle";
import { ApiError } from "fe-shared/src/types/common";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import { PermissionLevel } from "fe-shared/src/types/domain";
import { PickupRequestDetails } from "fe-shared-requests/src/types";
import { getRequest } from "fe-shared-requests/src/api/pickupRequests";

import useRequestsParams from "hooks/useRequestsParams";
import {
  PERMISSIONS,
  REQUESTS_QUERY_KEY,
  REQUESTS_NAVIGATION_QUERY_KEY,
  REQUEST_LIST_INFO_QUERY_KEY,
  NAVIGATION_PAGE_SIZE,
} from "consts";

import RequestDetailsView from "../RequestDetailsView";

import * as api from "../modules/api";

interface Props {
  setLastOpenedRequestId: (requestNumber: string) => void;
}

const RequestDetailsViewContainer = ({ setLastOpenedRequestId }: Props) => {
  const { requestNumber } = useParams<{ requestNumber: string }>();
  const { search } = useLocation();
  const { path } = useRouteMatch();
  const user = useContext(UserContext);
  const { requestParams } = useRequestsParams();

  const canViewRequestDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestDetails,
    level: PermissionLevel.Read,
  });
  const canViewRequests = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestsList,
    level: PermissionLevel.Read,
  });
  const returnUrl = useMemo(() => path.replace("/:requestNumber", ""), [path]);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [isWithNavigation, setIsWithNavigation] = useState(
    searchParams.get("navigation") === "true"
  );
  const { isDirectNavigation } = useNavigationType();

  useEffect(() => {
    if (requestNumber) {
      setLastOpenedRequestId(requestNumber);
    }
  }, [setLastOpenedRequestId, requestNumber]);

  const request = useQuery<PickupRequestDetails, AxiosError<ApiError>>(
    [REQUESTS_QUERY_KEY, requestNumber],
    () => getRequest(requestNumber),
    {
      enabled: canViewRequestDetails,
    }
  );

  const requestListInfo = useQuery<api.RequestListInfo, AxiosError<ApiError>>(
    [REQUEST_LIST_INFO_QUERY_KEY, requestNumber, requestParams],
    () => api.getRequestListInfo(requestNumber, requestParams),
    {
      enabled: canViewRequests && isWithNavigation && isDirectNavigation,
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  useEntityNavigationHandle(
    `${returnUrl}/${requestNumber}`,
    searchParams,
    setIsWithNavigation,
    requestNumber,
    requestListInfo.data?.currentIndex
  );

  const navigationRequestsQueryKey = [
    REQUESTS_NAVIGATION_QUERY_KEY,
    requestParams,
  ];

  const navigationRequests = useInfiniteQuery<
    api.NavigationRequests,
    AxiosError<ApiError>
  >(navigationRequestsQueryKey, api.getNavigationRequests, {
    enabled: canViewRequests && isWithNavigation,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * NAVIGATION_PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined,
  });

  const {
    data: navigationData,
    canFetchMore,
    isFetchingMore,
    fetchMore,
  } = navigationRequests;

  const navigation = isWithNavigation
    ? {
        numbers: (navigationData ?? []).reduce(
          (result, page) => [...result, ...page.requestNumbers],
          [] as string[]
        ),
        total: navigationData?.[0]?.total ?? 0,
        onNeedMore:
          canFetchMore &&
          !isFetchingMore &&
          (!isDirectNavigation ||
            (requestListInfo.data?.isPresent && !requestListInfo.isLoading))
            ? () => fetchMore()
            : undefined,
      }
    : undefined;

  return (
    <RequestDetailsView
      returnUrl={returnUrl}
      requestNumber={requestNumber}
      request={request}
      navigation={navigation}
    />
  );
};

export default RequestDetailsViewContainer;
